import { Status } from '../../models/common';
import { ProjectAbilityAsAdmin, ProjectAbilityAsDeveloper, ProjectFromDiscoveryService } from '../../models/project';
import {
    ProjectEnvironmentTypeAbilities,
    ProjectEnvironmentTypeAbilityAsAdmin,
    ProjectEnvironmentTypeAbilityAsDeveloper,
} from '../../models/project-environment-type';
import { StoreStateSelector } from './common';

export const getDoesProjectFromDiscoveryServiceHaveAuthorizationForAnyDevResource = (
    project: ProjectFromDiscoveryService
): boolean => {
    const { abilitiesAsDeveloper, abilitiesAsAdmin } = project ?? {};

    if (!abilitiesAsDeveloper && !abilitiesAsAdmin) {
        return false;
    }

    const hasDevBoxReadPermission = abilitiesAsDeveloper?.includes(ProjectAbilityAsDeveloper.ReadDevBoxesAsDeveloper);
    const hasDevBoxWritePermission = abilitiesAsDeveloper?.includes(ProjectAbilityAsDeveloper.WriteDevBoxesAsDeveloper);
    const hasEnvironmentReadPermission =
        abilitiesAsDeveloper?.includes(ProjectAbilityAsDeveloper.ReadEnvironmentsAsDeveloper) ||
        abilitiesAsAdmin?.includes(ProjectAbilityAsAdmin.ReadEnvironmentsAsAdmin);
    const hasEnvironmentWritePermission =
        abilitiesAsDeveloper?.includes(ProjectAbilityAsDeveloper.WriteEnvironmentsAsDeveloper) ||
        abilitiesAsAdmin?.includes(ProjectAbilityAsAdmin.WriteEnvironmentsAsAdmin);

    return (
        hasDevBoxReadPermission ||
        hasDevBoxWritePermission ||
        hasEnvironmentReadPermission ||
        hasEnvironmentWritePermission
    );
};

export const getIsProjectFromDiscoveryServiceAuthorizedForDevBoxCreate = (
    project: ProjectFromDiscoveryService
): boolean => {
    return (
        getIsProjectFromDiscoveryServiceAuthorizedForDevBoxRead(project) &&
        getIsProjectFromDiscoveryServiceAuthorizedForDevBoxWrite(project)
    );
};

export const getIsProjectFromDiscoveryServiceAuthorizedForDevBoxRead = (
    project: ProjectFromDiscoveryService
): boolean => {
    const { abilitiesAsDeveloper } = project ?? {};
    const hasDevBoxReadPermission = abilitiesAsDeveloper?.includes(ProjectAbilityAsDeveloper.ReadDevBoxesAsDeveloper);
    return hasDevBoxReadPermission ?? false;
};

export const getIsProjectFromDiscoveryServiceAuthorizedForDevBoxWrite = (
    project: ProjectFromDiscoveryService
): boolean => {
    const { abilitiesAsDeveloper } = project ?? {};
    const hasDevBoxWritePermission = abilitiesAsDeveloper?.includes(ProjectAbilityAsDeveloper.WriteDevBoxesAsDeveloper);
    return hasDevBoxWritePermission ?? false;
};

export const getIsProjectFromDiscoveryServiceAuthorizedForDevBoxCustomize = (
    project: ProjectFromDiscoveryService
): boolean => {
    const { abilitiesAsDeveloper } = project ?? {};
    const hasDevBoxCustomizePermission = abilitiesAsDeveloper?.includes(
        ProjectAbilityAsDeveloper.CustomizeDevBoxesAsDeveloper
    );
    return hasDevBoxCustomizePermission ?? false;
};

export const getIsProjectFromDiscoveryServiceAuthorizedForEnvironmentRead = (
    project: ProjectFromDiscoveryService
): boolean => {
    const { abilitiesAsDeveloper, abilitiesAsAdmin } = project ?? {};
    const hasEnvironmentReadPermission =
        abilitiesAsDeveloper?.includes(ProjectAbilityAsDeveloper.ReadEnvironmentsAsDeveloper) ||
        abilitiesAsAdmin?.includes(ProjectAbilityAsAdmin.ReadEnvironmentsAsAdmin);
    return hasEnvironmentReadPermission ?? false;
};

export const getIsProjectFromDiscoveryServiceAuthorizedForEnvironmentWrite = (
    project: ProjectFromDiscoveryService
): boolean => {
    const { abilitiesAsDeveloper, abilitiesAsAdmin } = project ?? {};
    const hasEnvironmentWritePermission =
        abilitiesAsDeveloper?.includes(ProjectAbilityAsDeveloper.WriteEnvironmentsAsDeveloper) ||
        abilitiesAsAdmin?.includes(ProjectAbilityAsAdmin.WriteEnvironmentsAsAdmin);
    return hasEnvironmentWritePermission ?? false;
};

export const getIsProjectEnvironmentTypeFromDataplaneAuthorizedForEnvironmentRead = (
    projectEnvironmentTypeAbilities: ProjectEnvironmentTypeAbilities
): boolean => {
    const { abilitiesAsDeveloper, abilitiesAsAdmin } = projectEnvironmentTypeAbilities ?? {};
    const hasEnvironmentReadPermission =
        abilitiesAsDeveloper?.includes(ProjectEnvironmentTypeAbilityAsDeveloper.ReadEnvironments) ||
        abilitiesAsAdmin?.includes(ProjectEnvironmentTypeAbilityAsAdmin.ReadEnvironments);
    return hasEnvironmentReadPermission ?? false;
};

export const getIsProjectEnvironmentTypeFromDataplaneAuthorizedForEnvironmentWrite = (
    projectEnvironmentTypeAbilities: ProjectEnvironmentTypeAbilities
): boolean => {
    const { abilitiesAsDeveloper, abilitiesAsAdmin } = projectEnvironmentTypeAbilities ?? {};
    const hasEnvironmentWritePermission =
        abilitiesAsDeveloper?.includes(ProjectEnvironmentTypeAbilityAsDeveloper.WriteEnvironments) ||
        abilitiesAsAdmin?.includes(ProjectEnvironmentTypeAbilityAsAdmin.WriteEnvironments);
    return hasEnvironmentWritePermission ?? false;
};

export const getStatusForDiscoverProjectEnvironmentTypeAbilities: StoreStateSelector<Status> = (store) =>
    store.projectEnvironmentTypeStore.status.discoverProjectEnvironmentTypeAbilities;
